"use client";

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import LinkHandlerFrame from "@/components/waves/LinkHandlerFrame";
import type { DropInteractionParams } from "@/components/waves/drops/drop.types";
import { DropLocation } from "@/components/waves/drops/drop.types";
import Drop from "@/components/waves/drops/Drop";
import WaveDropQuote from "@/components/waves/drops/WaveDropQuote";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiDropType } from "@/generated/models/ApiDropType";
import { convertApiDropToExtendedDrop, type ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useWaveById } from "@/hooks/useWaveById";
import { useApprovalWaveStatus } from "@/hooks/waves/useApprovalWaveStatus";
import { DROP_DETAIL_STALE_TIME_MS, fetchDropByIdBatched, getDropQueryKey } from "@/services/api/drop-api";
import { fetchQuorumParticipationDropPreviewBySerialNoV2 } from "@/services/api/quorum-participation-drop-preview-v2-api";
interface WaveDropLinkPreviewProps {
  readonly href: string;
  readonly waveId: string;
  readonly dropId?: string | undefined;
  readonly serialNo?: string | number | undefined;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
  readonly hideLink?: boolean | undefined;
}
const toSerialNumber = (serialNo: string | number | undefined): number | null => {
  if (typeof serialNo === "number") {
    return Number.isFinite(serialNo) ? serialNo : null;
  }
  if (!serialNo) {
    return null;
  }
  const parsed = Number.parseInt(serialNo, 10);
  return Number.isFinite(parsed) ? parsed : null;
};
const getErrorStatus = (error: unknown): number | undefined => {
  if (error === null || error === undefined || typeof error !== "object") {
    return undefined;
  }
  const maybeError = error as {
    readonly status?: unknown;
    readonly response?: {
      readonly status?: unknown;
    };
  };
  const status = maybeError.response?.status ?? maybeError.status;
  return typeof status === "number" ? status : undefined;
};
const isDropNotFoundError = (error: unknown, normalizedDropId: string | null): boolean => {
  if (error === null || error === undefined) {
    return false;
  }
  if (getErrorStatus(error) === 404) {
    return true;
  }
  if (!normalizedDropId) {
    return false;
  }
  const expectedMessage = `Drop ${normalizedDropId} not found`;
  if (error instanceof Error) {
    return error.message === expectedMessage;
  }
  return error === expectedMessage;
};
export default function WaveDropLinkPreview(t0) {
  const $ = _c(27);
  const {
    href,
    waveId,
    dropId,
    serialNo,
    onQuoteClick,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth,
    hideLink
  } = t0;
  const parsedSerialNo = toSerialNumber(serialNo);
  const normalizedDropId = dropId?.trim() ? dropId.trim() : null;
  const {
    data: drop,
    error
  } = useQuery({
    queryKey: normalizedDropId !== null ? getDropQueryKey(normalizedDropId) : [QueryKey.DROP, "wave-drop-link-preview", {
      dropId: normalizedDropId,
      waveId,
      serialNo: parsedSerialNo
    }],
    queryFn: async () => {
      if (normalizedDropId !== null) {
        return await fetchDropByIdBatched(normalizedDropId);
      }
      if (parsedSerialNo !== null) {
        return await fetchQuorumParticipationDropPreviewBySerialNoV2({
          waveId,
          serialNo: parsedSerialNo
        });
      }
      return null;
    },
    enabled: normalizedDropId !== null || parsedSerialNo !== null,
    placeholderData: keepPreviousData,
    staleTime: DROP_DETAIL_STALE_TIME_MS
  });
  const t1 = drop?.drop_type === ApiDropType.Participatory;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      enabled: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    wave
  } = useWaveById(waveId, t2);
  let t3;
  if ($[2] !== wave) {
    t3 = {
      wave
    };
    $[2] = wave;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    winningThreshold,
    winningThresholdMinDurationMs,
    isVotingClosed,
    isVotingControlsLocked
  } = useApprovalWaveStatus(t3);
  let t4;
  bb0: {
    if (drop?.drop_type !== ApiDropType.Participatory) {
      t4 = null;
      break bb0;
    }
    let t5;
    if ($[4] !== drop) {
      t5 = convertApiDropToExtendedDrop(drop);
      $[4] = drop;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    t4 = t5;
  }
  const extendedDrop = t4;
  const handleReply = _temp;
  let t5;
  if ($[6] !== onQuoteClick) {
    t5 = selectedDrop => onQuoteClick(selectedDrop);
    $[6] = onQuoteClick;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const handleDropContentClick = t5;
  const isNotFound = drop === null || isDropNotFoundError(error, normalizedDropId);
  let t6;
  if ($[8] !== drop || $[9] !== embedDepth || $[10] !== embedPath || $[11] !== extendedDrop || $[12] !== handleDropContentClick || $[13] !== handleReply || $[14] !== isNotFound || $[15] !== isVotingClosed || $[16] !== isVotingControlsLocked || $[17] !== maxEmbedDepth || $[18] !== onQuoteClick || $[19] !== quotePath || $[20] !== winningThreshold || $[21] !== winningThresholdMinDurationMs) {
    t6 = extendedDrop ? <Drop drop={extendedDrop} previousDrop={null} nextDrop={null} showWaveInfo={true} activeDrop={null} showReplyAndQuote={false} location={DropLocation.MY_STREAM} dropViewDropId={null} onReply={handleReply} onReplyClick={_temp2} onQuoteClick={onQuoteClick} onDropContentClick={handleDropContentClick} showInteractions={false} winningThreshold={winningThreshold} winningThresholdMinDurationMs={winningThresholdMinDurationMs} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} /> : <WaveDropQuote drop={isNotFound ? null : drop ?? null} partId={1} onQuoteClick={onQuoteClick} isNotFound={isNotFound} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[8] = drop;
    $[9] = embedDepth;
    $[10] = embedPath;
    $[11] = extendedDrop;
    $[12] = handleDropContentClick;
    $[13] = handleReply;
    $[14] = isNotFound;
    $[15] = isVotingClosed;
    $[16] = isVotingControlsLocked;
    $[17] = maxEmbedDepth;
    $[18] = onQuoteClick;
    $[19] = quotePath;
    $[20] = winningThreshold;
    $[21] = winningThresholdMinDurationMs;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== hideLink || $[24] !== href || $[25] !== t6) {
    t7 = <LinkHandlerFrame href={href} hideLink={hideLink}>{t6}</LinkHandlerFrame>;
    $[23] = hideLink;
    $[24] = href;
    $[25] = t6;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  return t7;
}
function _temp2() {}
function _temp(_params) {}